import { Typography } from "@/components/common/Typography/Typography"
import { freeTrialApplies } from "@/components/hoc/FreeTrialApplies"
import useFreeTrial from "@/hooks/useFreeTrial"
import { calculateProgress } from "@/hooks/useProgress"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUserLimits from "@/hooks/useUserLimits"
import { classNames } from "@/utils/classNames"
import FreeTrial from "./FreeTrial"

const clampPercent = (value: number) => Math.min(100, Math.max(0, value))

/**
 * Compact per-resource usage for trial users, shown under the days card.
 * Rows whose limit is 0 (unlimited, not loaded yet, or trial expired) or whose
 * usage is missing (a virtual limit) are hidden, and so is the whole card while
 * the trial status is unknown or there is no trial.
 */
export function UsageTrackerContent() {
  const { translate } = useTranslation()
  const limits = useUserLimits()
  const { hasTrial, isUnknown } = useFreeTrial()

  const rows = [
    {
      id: "questionnaires",
      label: translate("USAGE_TRACKER/QUESTIONNAIRES"),
      usage: limits.questionnairesUsage,
      limit: limits.questionnairesLimit,
    },
    {
      id: "files",
      label: translate("USAGE_TRACKER/FILES"),
      usage: limits.filesUsage,
      limit: limits.filesLimit,
    },
    {
      id: "chat",
      label: translate("USAGE_TRACKER/CHAT_MESSAGES"),
      usage: limits.chatMessagesUsage,
      limit: limits.chatMessagesLimit,
    },
    {
      id: "chat-daily",
      label: translate("USAGE_TRACKER/CHAT_DAILY"),
      usage: limits.chatMessagesDailyUsage,
      limit: limits.chatMessagesDailyLimit,
    },
    {
      id: "extension",
      label: translate("USAGE_TRACKER/EXTENSION_CALLS"),
      usage: limits.extensionCallsUsage,
      limit: limits.extensionCallsLimit,
    },
  ].filter((row) => row.limit > 0 && Number.isFinite(row.usage))

  if (!hasTrial || isUnknown || rows.length === 0) return null

  return (
    <FreeTrial.Card dataTestId="usage-tracker">
      <Typography variant="textXS-bold">
        {translate("USAGE_TRACKER/TITLE")}
      </Typography>
      <ul className="mt-2 flex flex-col gap-2">
        {rows.map((row) => (
          <li
            key={row.id}
            data-testid={`usage-tracker-${row.id}`}
            data-exhausted={row.usage >= row.limit}
          >
            <div className="flex justify-between gap-2">
              <Typography variant="textXS" className="truncate text-grey5">
                {row.label}
              </Typography>
              <Typography
                variant="textXS"
                className={classNames(
                  "whitespace-nowrap",
                  row.usage >= row.limit ? "text-red1" : "text-grey5",
                )}
              >
                {row.usage}/{row.limit}
              </Typography>
            </div>
            <FreeTrial.ProgressBar
              className="mt-1"
              hideIcon
              label={row.label}
              // usage can pass the limit (an Extended trial downgraded 20 -> 10)
              progressValue={clampPercent(
                calculateProgress(row.usage, row.limit),
              )}
              exhausted={row.usage >= row.limit}
            />
          </li>
        ))}
      </ul>
    </FreeTrial.Card>
  )
}

export const UsageTracker = freeTrialApplies(UsageTrackerContent)
